import { NavLink, useLocation } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import { useBranding } from "../../contexts/BrandingContext";
import { usePermissions } from "../../contexts/PermissionsContext";
import {
  LayoutDashboard, Calendar, Users, ShoppingCart, BarChart3,
  UserCog, Settings, LogOut, Scissors, ChevronLeft, ChevronRight, ChevronDown, MapPin,
  Megaphone, Shield, Search, HelpCircle
} from "lucide-react";
import { useState, useEffect } from "react";
import CommandPalette from "../CommandPalette";
import api from "../../lib/api";

const navStructure = [
  { to: "/dashboard", icon: LayoutDashboard, label: "Dashboard" },
  { label: "Schedule", icon: Calendar, children: [
    { to: "/calendar", label: "Calendar" },
    { to: "/schedule", label: "Shift Builder" },
    { to: "/operations", label: "Operations" },
    { to: "/tasks-board", label: "Tasks Board" },
    { to: "/color-library", label: "Color Bar" },
    { to: "/events", label: "Events & Groups" },
    { to: "/waitlist", label: "Waitlist" },
  ]},
  { label: "Clients", icon: Users, children: [
    { to: "/clients", label: "All Clients" },
    { to: "/client-scores", label: "Scores" },
    { to: "/score-alerts", label: "Alerts" },
    { to: "/noshow-risk", label: "No-Show Risk" },
  ]},
  { label: "Team", icon: UserCog, children: [
    { to: "/staff", label: "Staff" },
    { to: "/team-hub", label: "Team Hub" },
    { to: "/team-performance", label: "Performance" },
    { to: "/training", label: "Training" },
    { to: "/timesheets", label: "Timesheets" },
    { to: "/shift-briefing", label: "Shift Briefing" },
  ]},
  { label: "Sales", icon: ShoppingCart, children: [
    { to: "/pos", label: "Checkout" },
    { to: "/reports/cash-drawer", label: "Cash Drawer" },
    { to: "/memberships", label: "Memberships" },
    { to: "/loyalty", label: "Loyalty" },
    { to: "/referrals", label: "Referrals" },
    { to: "/gift-cards", label: "Gift Cards" },
    { to: "/inventory", label: "Inventory" },
  ]},
  { label: "Marketing", icon: Megaphone, children: [
    { to: "/marketing", label: "Campaigns" },
    { to: "/rebook", label: "Due for Rebook" },
    { to: "/automations", label: "Automations" },
    { to: "/reviews", label: "Reviews" },
    { to: "/forms", label: "Forms" },
  ]},
  { label: "Insights", icon: BarChart3, children: [
    { to: "/reports", label: "Reports" },
    { to: "/reports/cash-drawer", label: "Cash Drawer" },
    { to: "/executive", label: "Executive" },
    { to: "/location-pulse", label: "Location Pulse" },
    { to: "/command-center", label: "Command Center" },
  ]},
  { label: "Admin", icon: Shield, children: [
    { to: "/settings", label: "Settings" },
    { to: "/settings/billing", label: "Billing" },
    { to: "/integrations", label: "Integrations" },
    { to: "/webhooks", label: "Webhooks" },
    { to: "/white-label", label: "White Label" },
  ]},
  { to: "/help", icon: HelpCircle, label: "Help Center" },
];

export default function Sidebar() {
  const [collapsed, setCollapsed] = useState(false);
  const [cmdOpen, setCmdOpen] = useState(false);
  const [tenantName, setTenantName] = useState("");
  const { user, logout } = useAuth();
  const branding = useBranding();
  const { canViewNavItem, canViewNavGroup } = usePermissions();
  const location = useLocation();
  const logoSrc = branding.getLogoSrc();

  // Load current tenant (organization) name once per session so we can
  // show it under the logo. Kept intentionally lightweight — falls back
  // to silent when the endpoint isn't available.
  useEffect(() => {
    if (!user) { setTenantName(""); return; }
    let cancelled = false;
    api.get("/tenants/me")
      .then(r => { if (!cancelled) setTenantName(r.data?.business_name || ""); })
      .catch(() => { /* silent */ });
    return () => { cancelled = true; };
  }, [user]);

  // Global ⌘K / Ctrl+K shortcut
  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setCmdOpen(v => !v);
      }
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, []);

  const isChildActive = (children) => children?.some(c => location.pathname === c.to || location.pathname.startsWith(c.to + '/'));

  // Auto-expand only the group that contains the active route; collapse the rest.
  const activeGroupLabel = navStructure.find(i => i.children && isChildActive(i.children))?.label;
  const [openGroups, setOpenGroups] = useState(activeGroupLabel ? [activeGroupLabel] : []);

  // Whenever the route changes to a different group, switch the expanded group.
  useEffect(() => {
    if (activeGroupLabel && !openGroups.includes(activeGroupLabel)) {
      setOpenGroups([activeGroupLabel]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  const toggleGroup = (label) => {
    // Accordion behavior: opening one closes the others; clicking an open group closes it.
    setOpenGroups(prev => prev.includes(label) ? prev.filter(g => g !== label) : [label]);
  };

  return (
    <aside
      data-testid="app-sidebar"
      className={`h-screen sticky top-0 flex flex-col bg-white border-r border-slate-100 shadow-[2px_0_8px_rgba(0,0,0,0.02)] transition-all duration-300 ${
        collapsed ? "w-[72px]" : "w-[250px]"
      }`}
    >
      {/* Logo */}
      <div className="h-auto min-h-16 flex items-center px-4 py-3 border-b border-slate-100">
        {logoSrc ? (
          <img src={logoSrc} alt={branding.app_name} className="w-9 h-9 rounded-xl object-cover flex-shrink-0" data-testid="sidebar-logo" />
        ) : (
          <div className="w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0 gradient-primary" data-testid="sidebar-logo">
            <Scissors className="w-4 h-4 text-white" strokeWidth={1.5} />
          </div>
        )}
        {!collapsed && (
          <div className="ml-3 min-w-0 flex-1">
            <div className="font-['Outfit'] text-lg font-semibold tracking-tight text-slate-900 truncate" data-testid="sidebar-app-name">
              {branding.app_name}
            </div>
            {tenantName && tenantName !== branding.app_name && (
              <div
                className="font-['Manrope'] text-[11px] text-slate-400 truncate mt-0.5"
                title={tenantName}
                data-testid="sidebar-tenant-name">
                {tenantName}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Location indicator */}
      {!collapsed && (
        <div className="px-4 py-3 border-b border-slate-100">
          <div className="flex items-center gap-2 text-[11px] text-slate-400 uppercase tracking-[0.15em] font-semibold">
            <MapPin className="w-3 h-3" strokeWidth={1.5} />
            All Locations
          </div>
        </div>
      )}

      {/* Search trigger */}
      <div className={`px-3 pt-3 ${collapsed ? "" : "pb-1"}`}>
        <button
          data-testid="sidebar-search-trigger"
          onClick={() => setCmdOpen(true)}
          title="Search (⌘K)"
          className={`w-full flex items-center gap-2.5 rounded-xl border border-slate-100 bg-slate-50/70 hover:bg-slate-50 hover:border-slate-200 transition-all text-slate-400 hover:text-slate-600 ${
            collapsed ? "justify-center h-10" : "px-3 h-9 text-sm font-medium"
          }`}
        >
          <Search className="w-4 h-4 flex-shrink-0" strokeWidth={1.8} />
          {!collapsed && (
            <>
              <span className="flex-1 text-left text-[13px]">Search…</span>
              <kbd className="hidden sm:inline-flex items-center gap-0.5 text-[10px] text-slate-400 bg-white border border-slate-200 rounded px-1.5 py-0.5 font-mono font-semibold">⌘K</kbd>
            </>
          )}
        </button>
      </div>

      {/* Navigation */}
      <nav className="flex-1 py-3 px-3 space-y-0.5 overflow-y-auto">
        {navStructure.map((item) => {
          // Top-level nav item
          if (item.to) {
            if (!canViewNavItem(item.label)) return null;
            const isActive = location.pathname === item.to || location.pathname.startsWith(item.to + '/');
            return (
              <NavLink
                key={item.to}
                to={item.to}
                data-testid={`nav-${item.label.toLowerCase().replace(/\s/g, '-')}`}
                className={`flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-all duration-200 ${collapsed ? "justify-center" : ""} ${
                  isActive ? "bg-rose-50 text-rose-600" : "text-slate-500 hover:text-rose-600 hover:bg-rose-50/50"
                }`}
              >
                <item.icon className={`w-[18px] h-[18px] flex-shrink-0 ${isActive ? "text-rose-500" : ""}`} strokeWidth={1.5} />
                {!collapsed && <span>{item.label}</span>}
              </NavLink>
            );
          }

          // Group — hide if no children are visible
          if (!canViewNavGroup(item.label)) return null;
          const visibleChildren = item.children.filter(c => canViewNavItem(c.label));
          if (visibleChildren.length === 0) return null;

          const isOpen = openGroups.includes(item.label);
          const groupActive = isChildActive(visibleChildren);

          if (collapsed) {
            return (
              <div key={item.label} className="relative">
                <button
                  className={`flex items-center justify-center w-full py-2.5 rounded-xl transition-colors ${
                    groupActive ? "bg-rose-50 text-rose-500" : "text-slate-400"
                  }`}
                  title={item.label}
                >
                  <item.icon className="w-[18px] h-[18px]" strokeWidth={1.5} />
                </button>
              </div>
            );
          }

          return (
            <div key={item.label}>
              <button
                data-testid={`nav-group-${item.label.toLowerCase()}`}
                onClick={() => toggleGroup(item.label)}
                className={`flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-all duration-200 w-full ${
                  groupActive ? "text-rose-600" : "text-slate-500 hover:text-slate-700"
                }`}
              >
                <item.icon className={`w-[18px] h-[18px] flex-shrink-0 ${groupActive ? "text-rose-500" : ""}`} strokeWidth={1.5} />
                <span className="flex-1 text-left">{item.label}</span>
                <ChevronDown className={`w-3.5 h-3.5 transition-transform duration-200 ${isOpen ? "" : "-rotate-90"}`} />
              </button>
              {isOpen && (
                <div className="ml-5 pl-3 border-l-2 border-slate-100 space-y-0.5 mt-1 mb-1.5">
                  {visibleChildren.map(child => {
                    const childActive = location.pathname === child.to || location.pathname.startsWith(child.to + '/');
                    return (
                      <NavLink
                        key={child.to}
                        to={child.to}
                        data-testid={`nav-${child.label.toLowerCase().replace(/\s/g, '-')}`}
                        className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm transition-all duration-200 ${
                          childActive ? "bg-rose-50 text-rose-600 font-medium" : "text-slate-500 hover:text-rose-600 hover:bg-rose-50/50"
                        }`}
                      >
                        <span>{child.label}</span>
                      </NavLink>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </nav>

      {/* Collapse Toggle */}
      <button
        data-testid="sidebar-toggle"
        onClick={() => setCollapsed(!collapsed)}
        className="mx-3 mb-2 flex items-center justify-center py-2 rounded-xl text-slate-400 hover:text-rose-500 hover:bg-rose-50 transition-colors"
      >
        {collapsed ? <ChevronRight className="w-4 h-4" /> : <ChevronLeft className="w-4 h-4" />}
      </button>

      {/* User */}
      <div className="border-t border-slate-100 p-3">
        <div className={`flex items-center gap-3 ${collapsed ? "justify-center" : ""}`}>
          <div className="w-9 h-9 rounded-xl gradient-primary flex items-center justify-center flex-shrink-0 text-sm font-semibold text-white shadow-sm">
            {user?.name?.[0] || "U"}
          </div>
          {!collapsed && (
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-slate-800 truncate">{user?.name || "User"}</p>
              <p className="text-xs text-slate-400 truncate capitalize">{user?.role || "admin"}</p>
            </div>
          )}
          {!collapsed && (
            <button
              data-testid="logout-btn"
              onClick={logout}
              className="text-slate-400 hover:text-rose-500 transition-colors p-1.5 rounded-lg hover:bg-rose-50"
              title="Sign out"
            >
              <LogOut className="w-4 h-4" strokeWidth={1.5} />
            </button>
          )}
        </div>
      </div>

      {/* Command Palette */}
      <CommandPalette open={cmdOpen} onOpenChange={setCmdOpen} />
    </aside>
  );
}
